import React, { useState } from 'react';

interface ExternalResource {
  id: string;
  title: string;
  description: string;
  author?: string;
  thumbnailUrl?: string;
  sourceLibrary: string;
  sourceLibraryLogo: string;
  sourceUrl: string;
  type: 'book' | 'article' | 'thesis' | 'document' | 'audio' | 'video';
  availability: 'available' | 'restricted' | 'subscription';
}

interface IntegratedResourcesProps {
  resources: ExternalResource[];
  searchQuery?: string;
  onResourceClick: (resource: ExternalResource) => void;
  onSearch: (query: string) => void;
}

const IntegratedResources: React.FC<IntegratedResourcesProps> = ({
  resources,
  searchQuery = '',
  onResourceClick,
  onSearch
}) => {
  const [activeFilters, setActiveFilters] = useState<{
    libraries: string[];
    types: string[];
    availability: string[];
  }>({
    libraries: [],
    types: [],
    availability: []
  });

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Determină resursele de biblioteci unice din resurse pentru filtre
  const uniqueLibraries = Array.from(new Set(resources.map(resource => resource.sourceLibrary)));
  
  // Determină tipurile de resurse unice pentru filtre
  const uniqueTypes = Array.from(new Set(resources.map(resource => resource.type)));

  const handleFilterChange = (
    filterType: 'libraries' | 'types' | 'availability',
    value: string
  ) => {
    setActiveFilters(prevFilters => {
      const filters = { ...prevFilters };
      if (filters[filterType].includes(value)) {
        // Dacă filtrul este deja activ, îl eliminăm
        filters[filterType] = filters[filterType].filter(item => item !== value);
      } else {
        // Altfel, îl adăugăm
        filters[filterType] = [...filters[filterType], value];
      }
      return filters;
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(localSearchQuery);
  };

  // Filtrăm resursele pe baza filtrelor active
  const filteredResources = resources.filter(resource => {
    // Verificăm filtrele de bibliotecă
    if (activeFilters.libraries.length > 0 && !activeFilters.libraries.includes(resource.sourceLibrary)) {
      return false;
    }
    
    // Verificăm filtrele de tip
    if (activeFilters.types.length > 0 && !activeFilters.types.includes(resource.type)) {
      return false;
    }
    
    // Verificăm filtrele de disponibilitate
    if (activeFilters.availability.length > 0 && !activeFilters.availability.includes(resource.availability)) {
      return false;
    }
    
    return true;
  });

  // Determinăm grupurile de resurse pentru afișare
  const resourceGroups: Record<string, ExternalResource[]> = {};
  
  filteredResources.forEach(resource => {
    if (!resourceGroups[resource.sourceLibrary]) {
      resourceGroups[resource.sourceLibrary] = [];
    }
    resourceGroups[resource.sourceLibrary].push(resource);
  });

  // Funcție pentru obținerea iconului în funcție de tipul resursei
  const getResourceTypeIcon = (type: string) => {
    switch (type) {
      case 'book':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'article':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        );
      case 'thesis':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'document':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'audio':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m-2.828-9.9a9 9 0 0112.728 0M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
        );
      case 'video':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Funcție pentru obținerea culorii badge-ului pentru disponibilitate
  const getAvailabilityBadgeClasses = (availability: string) => {
    switch (availability) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'restricted':
        return 'bg-yellow-100 text-yellow-800';
      case 'subscription':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Funcție pentru obținerea textului pentru disponibilitate
  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'available':
        return 'Disponibil';
      case 'restricted':
        return 'Acces limitat';
      case 'subscription':
        return 'Abonament necesar';
      default:
        return 'Nedeterminat';
    }
  };

  return (
    <div className="container mx-auto px-4">
      <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-4 md:mb-0 md:mr-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-blue-800 mb-2">Resurse Integrate</h2>
            <p className="text-blue-700 mb-4">
              Explorează resurse din biblioteci și baze de date partenere. Accesul la unele resurse poate necesita autentificare sau abonament la biblioteca respectivă.
            </p>
          </div>
        </div>

        {/* Bara de căutare pentru resurse integrate */}
        <div className="mt-6">
          <form onSubmit={handleSearchSubmit}>
            <div className="flex rounded-md shadow-sm">
              <input 
                type="text" 
                className="flex-1 py-2 px-4 border border-gray-300 rounded-l-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Caută în toate bibliotecile partenere..."
                value={localSearchQuery} 
                onChange={(e) => setLocalSearchQuery(e.target.value)}
              />
              <button 
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white font-medium rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Caută
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar cu filtre */}
        <div className="lg:w-1/4">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Filtre</h3>
            
            {/* Filtrare după bibliotecă */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Biblioteci</h4>
              <div className="space-y-2">
                {uniqueLibraries.map(library => (
                  <div key={library} className="flex items-center">
                    <input 
                      type="checkbox" 
                      id={`library-${library}`}
                      checked={activeFilters.libraries.includes(library)}
                      onChange={() => handleFilterChange('libraries', library)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`library-${library}`} className="ml-2 text-gray-700">
                      {library}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Filtrare după tip */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Tip resursă</h4>
              <div className="space-y-2">
                {uniqueTypes.map(type => (
                  <div key={type} className="flex items-center">
                    <input 
                      type="checkbox" 
                      id={`type-${type}`}
                      checked={activeFilters.types.includes(type)}
                      onChange={() => handleFilterChange('types', type)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`type-${type}`} className="ml-2 flex items-center text-gray-700">
                      <span className="mr-2 text-gray-500">{getResourceTypeIcon(type)}</span>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Filtrare după disponibilitate */}
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Disponibilitate</h4>
              <div className="space-y-2">
                {['available', 'restricted', 'subscription'].map(availability => (
                  <div key={availability} className="flex items-center">
                    <input 
                      type="checkbox" 
                      id={`availability-${availability}`}
                      checked={activeFilters.availability.includes(availability)}
                      onChange={() => handleFilterChange('availability', availability)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`availability-${availability}`} className="ml-2 text-gray-700 flex items-center">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${availability === 'available' ? 'bg-green-500' : availability === 'restricted' ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
                      {getAvailabilityText(availability)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Lista de resurse */}
        <div className="lg:w-3/4">
          {Object.keys(resourceGroups).length === 0 ? (
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-medium text-gray-700 mb-2">Nu s-au găsit resurse</h3>
              <p className="text-gray-500">
                Nu există resurse care să corespundă filtrelor selectate. Încearcă să ajustezi filtrele sau să folosești un termen de căutare diferit.
              </p>
            </div>
          ) : (
            Object.entries(resourceGroups).map(([libraryName, libraryResources]) => (
              <div key={libraryName} className="mb-8">
                <div className="flex items-center mb-4">
                  <img 
                    src={libraryResources[0].sourceLibraryLogo} 
                    alt={`${libraryName} logo`}
                    className="h-8 w-8 object-contain mr-3"
                  />
                  <h3 className="text-xl font-bold text-gray-800">{libraryName}</h3>
                  <span className="ml-3 text-sm text-gray-500">
                    {libraryResources.length} resurse
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {libraryResources.map((resource) => (
                    <div 
                      key={resource.id}
                      className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition cursor-pointer"
                      onClick={() => onResourceClick(resource)}
                    >
                      <div className="flex h-full">
                        {resource.thumbnailUrl && (
                          <div className="w-1/3 bg-gray-200">
                            <img 
                              src={resource.thumbnailUrl} 
                              alt={resource.title} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className={`p-4 flex flex-col ${resource.thumbnailUrl ? 'w-2/3' : 'w-full'}`}>
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAvailabilityBadgeClasses(resource.availability)}`}>
                                {getAvailabilityText(resource.availability)}
                              </span>
                              <span className="text-gray-500">
                                {getResourceTypeIcon(resource.type)}
                              </span>
                            </div>
                            <h4 className="text-gray-800 font-medium mb-1 line-clamp-2">{resource.title}</h4>
                            {resource.author && (
                              <p className="text-sm text-gray-600 mb-2">de {resource.author}</p>
                            )}
                            <p className="text-sm text-gray-500 line-clamp-2">{resource.description}</p>
                          </div>
                          <div className="mt-3 flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {resource.sourceLibrary}
                            </span>
                            <div className="text-blue-600 text-sm">
                              Vizualizează
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegratedResources; 